import type { Workspace } from "./types";
import { predictAll } from "./predict";
import { buildCockpit } from "./cockpit";

/* AI Copilot — client mirror of api/src/llm/copilot.ts. The deterministic
 * intent router that answers grounded questions and proposes executable actions
 * when the API (and live Claude) aren't reachable. Byte-compatible intents. */

const usd = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export type CopilotActionKind = "create_task" | "add_note" | "send_digest";
export interface CopilotAction {
  kind: CopilotActionKind; label: string;
  accountId?: string; accountName?: string; title?: string; body?: string; priority?: "high" | "medium" | "low";
}
export interface CopilotReply {
  text: string; source: "llm" | "fallback"; model: string | null;
  actions: CopilotAction[]; refs: { label: string; accountId: string }[];
}

function findAccount(ws: Workspace, q: string) {
  const t = q.toLowerCase();
  return ws.accounts.find((a) => t.includes(a.name.toLowerCase()))
    ?? ws.accounts.find((a) => a.name.toLowerCase().split(/\s+/).some((w) => w.length > 3 && t.includes(w)));
}

function localSummary(ws: Workspace, accountId: string): string {
  const a = ws.accounts.find((x) => x.id === accountId);
  if (!a) return "I couldn't find that account.";
  const owner = ws.users.find((u) => u.id === a.ownerId)?.name ?? "the CSM";
  const util = a.seats ? Math.round((a.seatsActive / a.seats) * 100) : 0;
  const bandWord = a.healthBand === "good" ? "healthy" : a.healthBand === "watch" ? "needs attention" : "at risk";
  const rn = ws.renewals.find((r) => r.accountId === accountId);
  const openTasks = ws.tasks.filter((t) => t.accountId === accountId && t.status !== "done").length;
  const parts = [`${a.name} is ${bandWord} (health ${a.health}/100) on ${usd(a.arr)} ARR, owned by ${owner}.`, `Seat utilization is ${util}% (${a.seatsActive} of ${a.seats}).`];
  if (rn) parts.push(`Renews ${rn.renewalDate}, forecast "${rn.category}".`);
  if (openTasks) parts.push(`${openTasks} open task${openTasks === 1 ? "" : "s"} in flight.`);
  parts.push(a.healthBand === "risk" ? "Recommend a proactive check-in to de-risk the renewal." : "Candidate for an expansion conversation.");
  return parts.join(" ");
}

export function answerCopilotFallback(ws: Workspace, question: string): CopilotReply {
  const q = question.toLowerCase().trim();
  const reply = (text: string, actions: CopilotAction[] = [], refs: CopilotReply["refs"] = []): CopilotReply =>
    ({ text, source: "fallback", model: null, actions, refs });

  if (/(what can you|who are you|^help\b|\bhelp me\b|capabilit|what do you do|how do you work)/.test(q)) return helpReply(ws);

  const acct = findAccount(ws, question);
  if (acct && /(how|status|health|doing|tell me|about|summar)/.test(q)) {
    const actions: CopilotAction[] = [
      { kind: "add_note", label: `Draft a note on ${acct.name}`, accountId: acct.id, accountName: acct.name },
      { kind: "create_task", label: `Create a check-in task`, accountId: acct.id, accountName: acct.name, title: `Check in with ${acct.name}`, priority: acct.healthBand === "risk" ? "high" : "medium" },
    ];
    return reply(localSummary(ws, acct.id), actions, [{ label: acct.name, accountId: acct.id }]);
  }

  if (/(risk|churn|save|fire|red|unhealthy|worst)/.test(q)) {
    const top = predictAll(ws).filter((p) => p.churnRisk >= 35).slice(0, 5);
    if (!top.length) return reply("Good news — no accounts are showing elevated churn risk right now.");
    const lines = top.map((p) => `• *${p.accountName}* — ${p.churnRisk}% churn risk (${usd(p.arr)})${p.churnDrivers[0] ? `, driven by ${p.churnDrivers[0].label.toLowerCase()}` : ""}`);
    const worst = top[0];
    return reply(`${top.length} account${top.length > 1 ? "s" : ""} need attention, most-at-risk first:\n${lines.join("\n")}`, [
      { kind: "add_note", label: `Draft a save-play note on ${worst.accountName}`, accountId: worst.accountId, accountName: worst.accountName },
      { kind: "create_task", label: `Task: stabilize ${worst.accountName}`, accountId: worst.accountId, accountName: worst.accountName, title: `Stabilize ${worst.accountName} — churn risk ${worst.churnRisk}%`, priority: "high" },
    ], top.map((p) => ({ label: p.accountName, accountId: p.accountId })));
  }

  if (/(expand|expansion|upsell|grow|growth|upgrade|opportun)/.test(q)) {
    const top = [...predictAll(ws)].sort((a, b) => b.expansion - a.expansion).filter((p) => p.expansion >= 50).slice(0, 5);
    if (!top.length) return reply("No strong expansion candidates stand out right now.");
    const lines = top.map((p) => `• *${p.accountName}* — ${p.expansion}% expansion propensity (${usd(p.arr)})`);
    return reply(`Best expansion candidates:\n${lines.join("\n")}`, [
      { kind: "create_task", label: `Task: expansion conversation with ${top[0].accountName}`, accountId: top[0].accountId, accountName: top[0].accountName, title: `Open an expansion conversation with ${top[0].accountName}`, priority: "medium" },
    ], top.map((p) => ({ label: p.accountName, accountId: p.accountId })));
  }

  if (/(today|do now|to do|should i do|priorit|focus|cockpit|worklist|what.?s next|urgent)/.test(q)) {
    const c = buildCockpit(ws);
    const lines = c.items.slice(0, 5).map((i) => `• *${i.title}* — ${i.action}`);
    return reply(`You have ${c.summary.now} thing${c.summary.now === 1 ? "" : "s"} to do now (${usd(c.summary.arrNow)} ARR at stake). Top of the queue:\n${lines.join("\n") || "Nothing urgent — inbox zero. 🎉"}`,
      [{ kind: "send_digest", label: "Send this digest to Slack" }],
      c.items.slice(0, 5).map((i) => ({ label: i.accountName, accountId: i.accountId })));
  }

  if (/(renew|renewal|contract|expir)/.test(q)) {
    const soon = ws.renewals.filter((r) => r.stage !== "closed_won" && r.stage !== "closed_lost").slice(0, 5);
    const lines = soon.map((r) => `• *${r.accountName}* — ${usd(r.arr)} renews ${r.renewalDate} (${r.category})`);
    return reply(soon.length ? `Open renewals:\n${lines.join("\n")}` : "No open renewals in the pipeline.", [], soon.map((r) => ({ label: r.accountName, accountId: r.accountId })));
  }

  return helpReply(ws);
}

function helpReply(ws: Workspace): CopilotReply {
  const totalArr = ws.accounts.reduce((n, a) => n + a.arr, 0);
  return {
    source: "fallback", model: null, actions: [], refs: [],
    text: `I'm your customer-success copilot. I can see all ${ws.accounts.length} accounts (${usd(totalArr)} ARR) and help you:\n` +
      `• *Spot risk* — "who's at risk?"\n• *Find growth* — "expansion candidates"\n• *Plan your day* — "what should I do today?"\n` +
      `• *Check an account* — "how is Tidewater doing?"\nAnd I can act: create tasks, draft notes, and push a digest to Slack.`,
  };
}
